import { COUNTERPARTY_CONNECTED_TYPE } from '#client/constants/counterParty';
import { CHASE_BANK } from '#client/constants/transaction';
import { BASE_URL } from '#client/constants/url';
import { capitalizeFirstLetter, formatAccountNumber, getLast4digits } from '#client/utils/text';

import FieldReadView from '../../ReadOrEdit/FieldReadView';

export const checkValidFields = (data, fields = []) =>
  fields.every((fieldName) => data[fieldName] !== '');
export const createDataForAccountRadioButtons = (accounts, isSendMoney) =>
  accounts.map(({ id, bank, name, accountNumber, connectedType, accountType }) => {
    const isPlaidType = connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID;
    const label = () => {
      if (isSendMoney) {
        return (
          <FieldReadView
            title={isPlaidType ? bank : name}
            value={`${isPlaidType ? capitalizeFirstLetter(accountType) : bank} ${
              accountNumber ? getLast4digits(accountNumber) : ''
            }`}
          />
        );
      }
      return (
        <FieldReadView
          title={name}
          value={`${bank} ${accountNumber ? getLast4digits(accountNumber) : ''}`}
        />
      );
    };

    return {
      value: id,
      label: label()
    };
  });

export const convertCentToUSDText = (cent) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  return formatter.format(cent / 100);
};

export const buildForceLoginUrl = (idemKey, redirectTo) => {
  const mfaQueryParams = {
    forceMfa: true,
    idempotencyKey: idemKey,
    redirectTo
  };

  const mfaQueryString = new URLSearchParams(mfaQueryParams).toString();
  return `${BASE_URL}/dashboard/login?${mfaQueryString}`;
};
/**
 * Forces user login with Multi-Factor Authentication (MFA) before initiating a wire transfer.
 * Redirects the user to the login page with the necessary query parameters to enforce MFA.
 *
 * @param {string} idemKey - The idempotency key associated with the wire transfer request.
 * @param {string} redirectTo - The URL to redirect the user after successful login.
 */
export const forceLoginWireTransfer = (idemKey, redirectTo) => {
  const navigateTo = buildForceLoginUrl(idemKey, redirectTo);
  window.location.assign(navigateTo);
};

/**
 * Resets a specified parameter in the URL search params and replaces the current URL.
 *
 * @param {Function} setSearchParams - Function to update the URL search params.
 * @param {string} param - The parameter to reset in the URL search params.
 * @returns {void}
 */
export const resetCounterpartyOnUrl = (setSearchParams, param) =>
  setSearchParams(
    (prevParams) => {
      // Create a copy of the current search params
      const newParams = new URLSearchParams(prevParams);

      // Delete the specified parameter from the copied search params
      newParams.delete(param);

      return newParams;
    },
    { replace: true } // Replace the current URL without adding a new entry to the browser history
  );

/**
 * Check bank is Chase Bank or not
 * @param {object} param0
 * @param {string} param0.bank - bank name
 * @param {string } param0.connectedType - counter party connected type
 * @returns {boolean} - boolean value
 */
export const isChaseBank = ({ bank, connectedType }) => {
  if (!bank || !connectedType) {
    return false;
  }

  return (
    bank.toLowerCase().includes(CHASE_BANK.toLowerCase()) &&
    connectedType === COUNTERPARTY_CONNECTED_TYPE.PLAID
  );
};

/**
 *  Get card holder name
 * @param {object} param0
 * @param {string} param0.cardHolderFirstName - card holder first name
 * @param {string} param0.cardHolderLastName - card holder last name
 * @returns {string} - card holder name
 */
export const formatCardHolder = ({ cardHolderFirstName, cardHolderLastName }) => {
  if (!cardHolderFirstName || !cardHolderLastName) {
    return '';
  }

  return [cardHolderFirstName, cardHolderLastName].join(' ');
};

/**
 * Get the label option for card option
 * @param {object} param0
 * @param {string} param0.cardType - card type
 * @param {string} param0.lastFourDigits - last 4 digits card number
 * @returns {string} - the label option
 */
export const formatLabelCardOption = ({ cardType, lastFourDigits }) => {
  if (!cardType || !lastFourDigits) {
    return '';
  }

  return [
    capitalizeFirstLetter(cardType),
    formatAccountNumber({
      accountNumber: lastFourDigits,
      formatCharacter: '•',
      numberFormatCharacter: 2
    })
  ].join(' ');
};

/**
 * Detect by the error code
 * @param {object} param0
 * @param {string} param0.error - the error text
 * @param {string} param0.code - the error code
 * @param {string} param0.splitText - the character for spilt
 * @returns {boolean} - the text is match with error code or not
 */
export const detectErrorByCode = ({ error, code, splitText = '-' }) => {
  if (!error) {
    return false;
  }
  const errorParts = (error ?? '').split(splitText);

  return errorParts.some((part) => part?.trim() === code);
};
